import { Plus, X } from 'lucide-react'
import { Button, IconButton } from '@/components/ui/Button'
import { Input } from '@/components/ui/Field'
import { cn } from '@/lib/cn'

/**
 * One text value per row, add and remove inline. Used for additional domains
 * and for both sets of URL patterns.
 *
 * Empty rows are kept in state while editing and stripped on save — dropping
 * a row the moment its input clears makes the control unusable.
 */
export function RepeatableList({
  label,
  values,
  onChange,
  placeholder,
  addLabel = 'Add row',
  mono = false,
  hint,
}: {
  label: string
  values: string[]
  onChange: (next: string[]) => void
  placeholder?: string
  addLabel?: string
  mono?: boolean
  hint?: string
}) {
  return (
    <fieldset className="space-y-1.5">
      <legend className="field-label">{label}</legend>
      {hint && <p className="text-[12px] leading-4 text-ink-faint">{hint}</p>}

      <ul className="space-y-1.5">
        {values.map((value, i) => (
          <li key={i} className="flex items-center gap-1.5">
            <Input
              value={value}
              placeholder={placeholder}
              aria-label={`${label} ${i + 1}`}
              className={cn('h-8 text-[13px]', mono && 'font-mono')}
              onChange={(e) => onChange(values.map((v, j) => (j === i ? e.target.value : v)))}
            />
            <IconButton
              label={`Remove ${label} ${i + 1}`}
              className="h-8 w-8 shrink-0 hover:text-brand-red-700"
              onClick={() => onChange(values.filter((_, j) => j !== i))}
            >
              <X size={13} aria-hidden />
            </IconButton>
          </li>
        ))}
      </ul>

      <Button size="sm" variant="quiet" onClick={() => onChange([...values, ''])}>
        <Plus size={12} aria-hidden />
        {addLabel}
      </Button>
    </fieldset>
  )
}
